import * as React from 'react';

import {
  checkboxVariants,
  checkboxContainerVariants,
  checkboxWrapperVariants,
  checkboxInputVariants,
  checkboxIconVariants,
  checkboxLabelVariants,
  checkboxRequiredVariants,
  checkboxHelperTextVariants,
} from './Checkbox.styles';

import type { CheckboxProps } from './Checkbox.types';

/**
 * Checkbox component — Nabat Design System 2.0
 *
 * @example
 * ```tsx
 * <Checkbox label="Accept terms" />
 * <Checkbox label="Select all" indeterminate />
 * <Checkbox label="Email" required />
 * <Checkbox label="Required field" error="This field is required" />
 * ```
 */
export const Checkbox = React.forwardRef<React.ElementRef<'input'>, CheckboxProps>(
  (
    {
      label,
      indeterminate = false,
      scale = 'm',
      status: statusProp,
      helperText,
      error,
      required,
      checked,
      disabled,
      className,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<React.ElementRef<'input'>>(null);
    const status = error ? 'invalid' : statusProp || 'idle';
    const isChecked = Boolean(checked);
    const filled = isChecked || indeterminate;
    const displayText = error || helperText;

    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    React.useImperativeHandle(ref, () => inputRef.current as React.ElementRef<'input'>);

    return (
      <div className={checkboxVariants({ className })}>
        <label className={checkboxContainerVariants({ disabled, className: containerClassName })}>
          <span
            className={checkboxWrapperVariants({
              scale,
              status,
              checked: filled,
              disabled,
            })}
          >
            <input
              ref={inputRef}
              type="checkbox"
              checked={checked}
              disabled={disabled}
              required={required}
              aria-invalid={status === 'invalid' || undefined}
              className={checkboxInputVariants({ scale })}
              {...props}
            />
            <svg
              className={checkboxIconVariants({
                visible: isChecked && !indeterminate,
                scale,
              })}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M13.3333 4L6 11.3333L2.66667 8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <svg
              className={checkboxIconVariants({ visible: indeterminate, scale })}
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M3.33333 8H12.6667"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>

          {label && (
            // Inline, so the asterisk follows the label's last word rather than standing in a column.
            <span>
              <span className={checkboxLabelVariants({ scale, disabled })}>{label}</span>
              {required && (
                <span className={checkboxRequiredVariants()} aria-hidden="true">
                  *
                </span>
              )}
            </span>
          )}
        </label>

        {displayText && <p className={checkboxHelperTextVariants({ status })}>{displayText}</p>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';
